/**
 * The account and sync endpoints, from the browser's side.
 *
 * Every call sends cookies (`credentials: "same-origin"`), because the session
 * is an HttpOnly cookie the page cannot read. That is deliberate: a token in
 * localStorage is readable by any script that gets injected, and this one
 * cannot be.
 */

import type { PersistedState } from "@schema/persist.ts";

export interface SyncDocument {
  payload: PersistedState | null;
  version: number;
  updatedAt: number | null;
}

/** A push the server refused because someone else wrote first. */
export class VersionConflict extends Error {
  constructor(
    readonly remote: PersistedState | null,
    readonly version: number,
  ) {
    super("version conflict");
    this.name = "VersionConflict";
  }
}

const api = (path: string, init: RequestInit = {}) =>
  fetch(`/api${path}`, { credentials: "same-origin", ...init });

/** The signed-in address, or null. Never throws — being signed out is normal. */
export async function fetchAccount(): Promise<string | null> {
  try {
    const res = await api("/auth/me");
    if (!res.ok) return null;
    return ((await res.json()) as { email: string }).email;
  } catch {
    return null;
  }
}

/**
 * Ask for a sign-in link.
 *
 * Resolves whether or not an account exists — the server refuses to say, and
 * the UI must not imply otherwise. "Check your email" is the only honest
 * message, because it is the only thing we actually know.
 */
export async function requestSignIn(email: string, turnstileToken?: string): Promise<void> {
  const res = await api("/auth/request", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, turnstileToken }),
  });
  // 403 is the one failure the server answers honestly, because a failed bot
  // check says nothing about whether the address has an account. Everything
  // else is deliberately indistinguishable.
  if (res.status === 403) {
    throw new Error("Couldn't verify you're human. Try again, or reload the page.");
  }
  if (!res.ok) throw new Error("Couldn't send the link. Try again in a moment.");
}

export async function signOut(): Promise<void> {
  await api("/auth/signout", { method: "POST" });
}

export async function pullState(): Promise<SyncDocument> {
  const res = await api("/sync");
  if (!res.ok) throw new Error("Couldn't reach your synced data.");
  return (await res.json()) as SyncDocument;
}

/**
 * Store a document, declaring the version it was built from.
 *
 * Throws {@link VersionConflict} carrying the server's copy when another
 * device wrote first, so the caller can merge and retry rather than guess.
 */
export async function pushState(payload: PersistedState, version: number): Promise<number> {
  const res = await api("/sync", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ payload, version }),
  });

  if (res.status === 409) {
    const body = (await res.json()) as { payload: PersistedState | null; version: number };
    throw new VersionConflict(body.payload, body.version);
  }
  if (!res.ok) throw new Error("Couldn't save your progress.");
  return ((await res.json()) as { version: number }).version;
}

export async function deleteAccount(confirmEmail: string): Promise<void> {
  const res = await api("/account", {
    method: "DELETE",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ confirmEmail }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Couldn't delete the account.");
  }
}
